import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { getStoredApiKey, clearStoredApiKey, maskApiKey } from './apiKeySession';
import { activeRemoteBackend } from './devBackend/client';

// ============================================================================
// Types
// ============================================================================

interface AuthnMeUser {
  userId: string;
  tenantId: string;
  email?: string;
  roles?: string[];
}

interface AuthnMeContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  isConfigured: boolean; // True if AuthnMe is configured (org ID set)
  hasEverLoggedIn: boolean; // True if user has ever logged in before (persisted in localStorage)
  user: AuthnMeUser | null;
  token: string | null;
  /** Customer API key driving this session (from a `/?apikey=` link), else null. */
  apiKey: string | null;
  /** How the browser is signed in: AuthnMe JWT, a customer API key, or not at all. */
  authMethod: 'jwt' | 'api_key' | null;
  error: string | null;
  login: () => Promise<void>;
  loginWithGoogle: () => void;
  loginWithEmail: (email: string) => Promise<{ success: boolean; webauthnAvailable?: boolean; message?: string }>;
  loginWithPasskey: () => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
  getAuthHeaders: () => Record<string, string>;
  // Fetch wrapper that auto-refreshes token on 401
  fetchWithAuth: (url: string, options?: RequestInit) => Promise<Response>;
  // Force token refresh
  refreshToken: () => Promise<boolean>;
  // Ensure token is fresh before critical operations (returns false if refresh failed)
  ensureTokenFresh: () => Promise<boolean>;
}

interface AuthnMeProviderProps {
  children: ReactNode;
  orgId?: string;
  redirectUri?: string;
}

// ============================================================================
// Context
// ============================================================================

const AuthnMeContext = createContext<AuthnMeContextType | null>(null);

// ============================================================================
// Configuration
// ============================================================================

// Fallback values (used before backend config is fetched)
const AUTHNME_ORG_ID_FALLBACK =
  import.meta.env.VITE_AUTHNME_ORG_ID ||
  import.meta.env.REACT_APP_AUTHNME_ORG_ID ||
  '';
const AUTHNME_REDIRECT_URI_FALLBACK =
  import.meta.env.VITE_AUTHNME_REDIRECT_URI ||
  import.meta.env.REACT_APP_AUTHNME_REDIRECT_URI ||
  window.location.origin;
const AUTHNME_BASE_URL_FALLBACK =
  import.meta.env.VITE_AUTHNME_BASE_URL ||
  import.meta.env.REACT_APP_AUTHNME_BASE_URL ||
  'https://authn.me';
const TOKEN_STORAGE_KEY = 'authnme_token';
const REFRESH_TOKEN_STORAGE_KEY = 'authnme_refresh_token';
const USER_STORAGE_KEY = 'authnme_user';
const HAS_EVER_LOGGED_IN_KEY = 'authnme_has_logged_in';

// Legacy storage keys (for migration)
const LEGACY_TOKEN_KEY = 'access_token';
const LEGACY_REFRESH_TOKEN_KEY = 'refresh_token';

// Debug: Request short-lived tokens for testing refresh flow
// Set to 0 to disable (use server default). Set to e.g. 10 for 10-second tokens.
const DEBUG_TOKEN_EXPIRY_SECONDS = 0;

// Guard: prevent concurrent debug swap attempts (React Strict Mode double-renders)
let _debugSwapInProgress = false;

// Backend config type
interface BackendAuthConfig {
  orgId: string;
  baseUrl: string;
  redirectUri: string;
  configured: boolean;
}

// Backend config cache (fetched once and reused)
let backendAuthConfig: BackendAuthConfig | null = null;
let backendConfigPromise: Promise<BackendAuthConfig | null> | null = null;

// Fetch auth config from backend (cached)
async function fetchBackendAuthConfig(): Promise<BackendAuthConfig | null> {
  if (backendAuthConfig) return backendAuthConfig;
  if (backendConfigPromise) return backendConfigPromise;

  backendConfigPromise = fetch('/api/auth/config')
    .then(res => res.ok ? res.json() : null)
    .then(data => {
      if (data) {
        backendAuthConfig = {
          orgId: data.org_id || '',
          baseUrl: data.base_url || AUTHNME_BASE_URL_FALLBACK,
          // A relayed deployment (localhost backend switcher, devBackend/)
          // reports its own origin; sign-in has to land back on this page.
          redirectUri: (activeRemoteBackend() ? '' : data.redirect_uri) || window.location.origin,
          configured: data.configured || false,
        };
        console.log('[AuthnMe] Backend config loaded:', backendAuthConfig);
      }
      return backendAuthConfig;
    })
    .catch(err => {
      console.warn('[AuthnMe] Failed to fetch backend config:', err);
      return null;
    });

  return backendConfigPromise;
}

// Check if AuthnMe is configured (synchronous check using fallback)
export function isAuthnMeConfigured(): boolean {
  // Use backend config if available, otherwise fall back to env var
  if (backendAuthConfig) return backendAuthConfig.configured;
  return AUTHNME_ORG_ID_FALLBACK !== '';
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Parse JWT token and extract claims (without verification - verification is done server-side)
 */
function parseJwtClaims(token: string): AuthnMeUser | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = JSON.parse(atob(parts[1]));
    return {
      userId: payload.user_id || payload.sub,
      tenantId: payload.tenant_id,
      email: payload.email,
      roles: payload.roles,
    };
  } catch {
    return null;
  }
}

/**
 * Convert base64url string to ArrayBuffer (for WebAuthn)
 */
function base64UrlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padding = '='.repeat((4 - base64.length % 4) % 4);
  const binary = atob(base64 + padding);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Convert ArrayBuffer to base64url string (for WebAuthn)
 */
function bufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Get token TTL in seconds (time from now until expiry)
 */
function getTokenTTL(token: string): number {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return 0;
    const payload = JSON.parse(atob(parts[1]));
    const exp = payload.exp;
    if (!exp) return 0;
    return Math.max(0, (exp * 1000 - Date.now()) / 1000);
  } catch {
    return 0;
  }
}

/**
 * Get the total lifespan of a token (exp - iat) in seconds
 */
function getTokenLifespan(token: string): number {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return 0;
    const payload = JSON.parse(atob(parts[1]));
    const exp = payload.exp;
    const iat = payload.iat;
    if (!exp || !iat) return 3600; // default 1 hour if unknown
    return exp - iat;
  } catch {
    return 3600;
  }
}

/**
 * Check if JWT token is expired
 * Uses proportional buffer: min(30s, 20% of token lifespan)
 */
function isTokenExpired(token: string): boolean {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;

    const payload = JSON.parse(atob(parts[1]));
    const exp = payload.exp;
    if (!exp) return true;

    // Proportional buffer: min(30s, 20% of token lifespan)
    const lifespan = getTokenLifespan(token);
    const buffer = Math.min(30000, lifespan * 200); // 20% of lifespan in ms
    return Date.now() >= (exp * 1000) - buffer;
  } catch {
    return true;
  }
}

/**
 * Store token (using localStorage to persist across tabs)
 */
function storeToken(token: string): void {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
}

/**
 * Get stored token (checks both new and legacy keys)
 */
function getStoredToken(): string | null {
  // Check new key first
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (token) return token;

  // Fall back to legacy key
  const legacyToken = localStorage.getItem(LEGACY_TOKEN_KEY);
  if (legacyToken) {
    console.log('[AuthnMe] Migrating token from legacy storage key');
    // Migrate to new key
    localStorage.setItem(TOKEN_STORAGE_KEY, legacyToken);
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    return legacyToken;
  }

  return null;
}

/**
 * Clear stored token (clears both new and legacy keys)
 */
function clearStoredToken(): void {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(USER_STORAGE_KEY);
  // Also clear legacy keys
  localStorage.removeItem(LEGACY_TOKEN_KEY);
  localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
}

// ============================================================================
// Provider Component
// ============================================================================

export function AuthnMeProvider({ children, orgId, redirectUri }: AuthnMeProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isConfigured, setIsConfigured] = useState(false); // True when AuthnMe org ID is configured
  const [hasEverLoggedIn, setHasEverLoggedIn] = useState(() => localStorage.getItem(HAS_EVER_LOGGED_IN_KEY) === 'true');
  const [user, setUser] = useState<AuthnMeUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Backend config state
  const [authConfig, setAuthConfig] = useState<{
    orgId: string;
    baseUrl: string;
    redirectUri: string;
  }>({
    orgId: orgId || AUTHNME_ORG_ID_FALLBACK,
    baseUrl: AUTHNME_BASE_URL_FALLBACK,
    redirectUri: redirectUri || AUTHNME_REDIRECT_URI_FALLBACK,
  });

  const effectiveOrgId = authConfig.orgId;
  const effectiveRedirectUri = authConfig.redirectUri;
  const AUTHNME_BASE_URL = authConfig.baseUrl;

  // Fetch backend config and check for existing token on mount
  useEffect(() => {
    const init = async () => {
      // Fetch backend config first
      const config = await fetchBackendAuthConfig();
      if (config) {
        setAuthConfig({
          orgId: orgId || config.orgId,
          baseUrl: config.baseUrl,
          redirectUri: redirectUri || config.redirectUri,
        });
        // Set configured state based on backend config or fallback
        setIsConfigured(config.configured || (orgId || config.orgId) !== '');
      } else {
        // No backend config, check if env fallback has org ID
        setIsConfigured(AUTHNME_ORG_ID_FALLBACK !== '' || (orgId || '') !== '');
      }

      console.log('[AuthnMe] Config:', {
        orgId: config?.orgId || AUTHNME_ORG_ID_FALLBACK,
        redirectUri: config?.redirectUri || AUTHNME_REDIRECT_URI_FALLBACK,
        baseUrl: config?.baseUrl || AUTHNME_BASE_URL_FALLBACK,
        isConfigured: config?.configured || AUTHNME_ORG_ID_FALLBACK !== '',
        source: config ? 'backend' : 'env',
      });

      // API-key session (`/?apikey=vox_…`, see apiKeySession.ts). App.tsx moved
      // the key from the URL into localStorage before React mounted. It wins
      // over any JWT still stored here — the link says who to be. There are no
      // claims to read a name or customer from, so /api/auth/whoami supplies
      // them; the key itself never expires, so nothing is scheduled.
      const storedApiKey = getStoredApiKey();
      if (storedApiKey) {
        clearStoredToken();
        localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
        try {
          const who = await fetch('/api/auth/whoami', { headers: { 'X-API-Key': storedApiKey } });
          if (who.ok) {
            const data = await who.json();
            setApiKey(storedApiKey);
            setToken(null);
            setUser({
              userId: `apikey:${data.customer_id || ''}`,
              tenantId: '',
              email: data.customer_name || `API key ${maskApiKey(storedApiKey)}`,
              roles: [],
            });
            setIsAuthenticated(true);
            setHasEverLoggedIn(true);
            localStorage.setItem(HAS_EVER_LOGGED_IN_KEY, 'true');
            console.log('[AuthnMe] API-key session for customer', data.customer_id);
          } else if (who.status === 401 || who.status === 403) {
            // Revoked (or never valid): forget it so the next load shows login.
            console.log('[AuthnMe] Stored API key rejected (status', who.status, '), clearing');
            clearStoredApiKey();
            setError('This access link is no longer valid — its API key was revoked.');
          } else {
            console.log('[AuthnMe] whoami failed (status', who.status, '), keeping API key for a later retry');
          }
        } catch (err) {
          console.error('[AuthnMe] whoami network error (keeping API key):', err);
        }
        setIsLoading(false);
        return;
      }

      // Check for existing valid token
      const storedToken = getStoredToken();

      // Check both new and legacy refresh token keys
      let storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
      if (!storedRefreshToken) {
        const legacyRefreshToken = localStorage.getItem(LEGACY_REFRESH_TOKEN_KEY);
        if (legacyRefreshToken) {
          console.log('[AuthnMe] Migrating refresh token from legacy storage key');
          localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, legacyRefreshToken);
          localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
          storedRefreshToken = legacyRefreshToken;
        }
      }

      if (storedToken && !isTokenExpired(storedToken)) {
        // Token is still valid
        const claims = parseJwtClaims(storedToken);
        if (claims) {
          // Debug mode: if token lifespan is longer than requested, swap it for a short-lived one
          // Guard against concurrent calls (React Strict Mode double-renders)
          if (DEBUG_TOKEN_EXPIRY_SECONDS > 0 && storedRefreshToken && getTokenLifespan(storedToken) > DEBUG_TOKEN_EXPIRY_SECONDS * 2 && !_debugSwapInProgress) {
            _debugSwapInProgress = true;
            console.log(`[AuthnMe] Debug: swapping ${getTokenLifespan(storedToken)}s token for ${DEBUG_TOKEN_EXPIRY_SECONDS}s token`);
            try {
              // Read refresh token fresh from localStorage (may have been updated by concurrent call)
              const freshRefreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
              if (!freshRefreshToken) {
                console.warn('[AuthnMe] Debug swap: refresh token disappeared');
                _debugSwapInProgress = false;
              } else {
                const swapResp = await fetch('/api/auth/refresh', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ refresh_token: freshRefreshToken, requested_expires_in: DEBUG_TOKEN_EXPIRY_SECONDS }),
                });
                if (swapResp.ok) {
                  const swapData = await swapResp.json();
                  if (swapData.access_token) {
                    storeToken(swapData.access_token);
                    if (swapData.refresh_token) {
                      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, swapData.refresh_token);
                    }
                    const shortClaims = parseJwtClaims(swapData.access_token);
                    if (shortClaims) {
                      setToken(swapData.access_token);
                      setUser(shortClaims);
                      setIsAuthenticated(true);
                      setHasEverLoggedIn(true);
                      localStorage.setItem(HAS_EVER_LOGGED_IN_KEY, 'true');
                      console.log(`[AuthnMe] Debug: swapped to ${getTokenLifespan(swapData.access_token)}s token (TTL: ${getTokenTTL(swapData.access_token).toFixed(1)}s)`);
                      _debugSwapInProgress = false;
                      setIsLoading(false);
                      return; // Skip the normal flow below
                    }
                  }
                }
                _debugSwapInProgress = false;
              }
            } catch (err) {
              _debugSwapInProgress = false;
              console.warn('[AuthnMe] Debug swap failed, using original token:', err);
            }
          }

          setToken(storedToken);
          setUser(claims);
          setIsAuthenticated(true);
          setHasEverLoggedIn(true);
          localStorage.setItem(HAS_EVER_LOGGED_IN_KEY, 'true');
          console.log('[AuthnMe] Using stored valid token');
        } else {
          clearStoredToken();
        }
        setIsLoading(false);
      } else if (storedRefreshToken) {
        // Token expired but we have a refresh token - try to refresh
        console.log('[AuthnMe] Stored token expired, attempting refresh...');
        try {
          const refreshBody: Record<string, any> = { refresh_token: storedRefreshToken };
          if (DEBUG_TOKEN_EXPIRY_SECONDS > 0) {
            refreshBody.requested_expires_in = DEBUG_TOKEN_EXPIRY_SECONDS;
          }
          const response = await fetch('/api/auth/refresh', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(refreshBody),
          });

          if (response.ok) {
            const data = await response.json();
            if (data.access_token) {
              storeToken(data.access_token);
              if (data.refresh_token) {
                localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, data.refresh_token);
              }
              const claims = parseJwtClaims(data.access_token);
              if (claims) {
                setToken(data.access_token);
                setUser(claims);
                setIsAuthenticated(true);
                setHasEverLoggedIn(true);
                localStorage.setItem(HAS_EVER_LOGGED_IN_KEY, 'true');
                console.log('[AuthnMe] Token refreshed on startup');
              }
            }
          } else {
            // Only clear refresh token on 401 (truly invalid token)
            // For other errors (500, network issues), keep the refresh token for later retry
            if (response.status === 401 || response.status === 403) {
              console.log('[AuthnMe] Refresh token rejected on startup (status', response.status, '), clearing tokens');
              clearStoredToken();
              localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
            } else {
              console.log('[AuthnMe] Refresh failed on startup (status', response.status, '), keeping refresh token for later retry');
              clearStoredToken(); // Clear the expired access token only
            }
          }
        } catch (err) {
          // Network error - keep the refresh token, might work later
          console.error('[AuthnMe] Refresh network error on startup (keeping refresh token):', err);
          clearStoredToken(); // Clear the expired access token only
        }
        setIsLoading(false);
      } else {
        // No token or refresh token. The "has logged in" flag used to be
        // write-once, which left every signed-out browser permanently labelled
        // "Sign In"; with nothing to restore, the CTA should offer "Sign Up".
        localStorage.removeItem(HAS_EVER_LOGGED_IN_KEY);
        setHasEverLoggedIn(false);
        setIsLoading(false);
      }
    };

    init();
  }, [orgId, redirectUri]);

  // Handle OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // AuthnMe returns access_token and refresh_token. `?token=` is a legacy
    // alias only used when access_token is absent — and we must not strip
    // `?token=` from the URL otherwise, because partner pages (e.g.
    // /accept-tenant?token=<switch-invite>) use it for their own purpose.
    const accessTokenParam = params.get('access_token');
    const legacyTokenIsJwt = !accessTokenParam;
    const authToken = accessTokenParam || params.get('token');
    const refreshToken = params.get('refresh_token');
    const authError = params.get('error');
    const enrollmentRequired = params.get('enrollment_required');
    const enrollmentToken = params.get('enrollment_token');
    const userEmail = params.get('user_email');

    // Debug logging for auth callback
    if (authToken || authError || enrollmentRequired) {
      console.log('[AuthnMe] Callback received:', {
        hasToken: !!authToken,
        hasRefreshToken: !!refreshToken,
        error: authError,
        enrollmentRequired,
        url: window.location.href,
      });
    }

    // If enrollment is required, redirect to AuthnMe embed page for passkey setup
    if (enrollmentRequired === 'true' && enrollmentToken) {
      const embedUrl = new URL(`${AUTHNME_BASE_URL}/embed/${effectiveOrgId}`);
      embedUrl.searchParams.set('enrollment_required', 'true');
      embedUrl.searchParams.set('enrollment_token', enrollmentToken);
      if (userEmail) {
        embedUrl.searchParams.set('user_email', userEmail);
      }
      embedUrl.searchParams.set('redirect_uri', window.location.origin);
      window.location.href = embedUrl.toString();
      return;
    }

    if (authToken) {
      // Got token from OAuth callback
      if (!isTokenExpired(authToken)) {
        const claims = parseJwtClaims(authToken);
        if (claims) {
          storeToken(authToken);
          // A real login replaces an API-key session, if this browser had one.
          clearStoredApiKey();
          setApiKey(null);
          if (refreshToken) {
            localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
            console.log('[AuthnMe] Stored refresh token from callback');
          }
          setToken(authToken);
          setUser(claims);
          setIsAuthenticated(true);
          setHasEverLoggedIn(true);
          localStorage.setItem(HAS_EVER_LOGGED_IN_KEY, 'true');

          // Clean up URL. Only strip `?token=` when it was the JWT carrier
          // (legacy flow with no access_token); otherwise leave it for the
          // page itself (e.g. /accept-tenant?token=<switch-invite>).
          const url = new URL(window.location.href);
          url.searchParams.delete('access_token');
          url.searchParams.delete('refresh_token');
          if (legacyTokenIsJwt) {
            url.searchParams.delete('token');
          }
          window.history.replaceState({}, '', url.toString());
        }
      }
      setIsLoading(false);
    } else if (authError) {
      setError(authError);
      const url = new URL(window.location.href);
      url.searchParams.delete('error');
      window.history.replaceState({}, '', url.toString());
      setIsLoading(false);
    }
  }, [effectiveOrgId, AUTHNME_BASE_URL]);

  // Track if refresh is in progress to avoid concurrent refreshes
  const refreshPromiseRef = React.useRef<Promise<boolean> | null>(null);

  // Function to refresh the access token
  const refreshAccessToken = useCallback(async (): Promise<boolean> => {
    // If a refresh is already in progress, wait for it
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    // Check both new and legacy refresh token keys
    let storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    if (!storedRefreshToken) {
      const legacyRefreshToken = localStorage.getItem(LEGACY_REFRESH_TOKEN_KEY);
      if (legacyRefreshToken) {
        console.log('[AuthnMe] Migrating refresh token from legacy storage key');
        localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, legacyRefreshToken);
        localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
        storedRefreshToken = legacyRefreshToken;
      }
    }

    if (!storedRefreshToken) {
      console.log('[AuthnMe] No refresh token available');
      return false;
    }

    console.log('[AuthnMe] Refreshing access token...');

    const refreshPromise = (async (): Promise<boolean> => {
      try {
        const refreshBody: Record<string, any> = { refresh_token: storedRefreshToken };
        if (DEBUG_TOKEN_EXPIRY_SECONDS > 0) {
          refreshBody.requested_expires_in = DEBUG_TOKEN_EXPIRY_SECONDS;
        }
        const response = await fetch('/api/auth/refresh', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(refreshBody),
        });

        if (!response.ok) {
          console.error('[AuthnMe] Token refresh failed:', response.status);
          // If refresh token is invalid, clear it
          if (response.status === 401) {
            localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
          }
          return false;
        }

        const data = await response.json();
        if (data.access_token) {
          // Store new tokens
          storeToken(data.access_token);
          if (data.refresh_token) {
            localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, data.refresh_token);
          }

          // Update state
          const claims = parseJwtClaims(data.access_token);
          if (claims) {
            setToken(data.access_token);
            setUser(claims);
            setIsAuthenticated(true);
            console.log('[AuthnMe] Token refreshed successfully');
            return true;
          }
        }
        return false;
      } catch (err) {
        console.error('[AuthnMe] Token refresh error:', err);
        return false;
      } finally {
        refreshPromiseRef.current = null;
      }
    })();

    refreshPromiseRef.current = refreshPromise;
    return refreshPromise;
  }, []);

  // Automatic teardown (refresh token rejected / expired): drop OUR session
  // only. The AuthnMe SSO cookie is left alone on purpose so the next "Sign
  // In" silently restores the same user. Defined early so effects can use it.
  const clearLocalSession = useCallback(() => {
    clearStoredToken();
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    // An API-key session only forgets the key; the key itself stays valid
    // until revoked on the Customer page.
    clearStoredApiKey();
    setApiKey(null);
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
  }, []);

  // Explicit, user-initiated sign out.
  //
  // Clearing our own localStorage is not enough: AuthnMe keeps its own session
  // cookie on its origin, and its /login page auto-completes when that cookie
  // is present and the tenant is already consented ("Welcome back, …! Signing
  // you in…"), bouncing straight back with fresh tokens. The account chooser
  // never renders, so after signing out you can only ever sign back in as the
  // same user — never sign up as a new one. So end the AuthnMe session too:
  //   POST /auth/logout          { refresh_token }        revokes the token
  //   POST /auth/session/logout  credentials: 'include'   clears the SSO cookie
  // Both are best-effort — the local session is dropped either way.
  const logout = useCallback(() => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);

    clearLocalSession();

    // A returning user who deliberately signed out is back to an open choice,
    // so let the CTA offer "Sign Up" again instead of latching on "Sign In".
    localStorage.removeItem(HAS_EVER_LOGGED_IN_KEY);
    setHasEverLoggedIn(false);

    void (async () => {
      if (refreshToken) {
        try {
          await fetch(`${AUTHNME_BASE_URL}/auth/logout`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: refreshToken }),
          });
        } catch (err) {
          console.warn('[AuthnMe] Refresh token revocation failed:', err);
        }
      }
      try {
        await fetch(`${AUTHNME_BASE_URL}/auth/session/logout`, {
          method: 'POST',
          credentials: 'include',
        });
        console.log('[AuthnMe] AuthnMe session ended');
      } catch (err) {
        // Cross-site cookie writes can be blocked (Safari ITP, strict privacy
        // settings). We are still signed out locally; the AuthnMe session may
        // survive and silently restore this user on the next sign in.
        console.warn('[AuthnMe] Could not end AuthnMe session:', err);
      }
    })();
  }, [clearLocalSession, AUTHNME_BASE_URL]);

  // Refresh token before expiry (with retry logic - don't logout on first failure)
  useEffect(() => {
    if (!token || !isAuthenticated) return;

    // Parse token to get expiry
    try {
      const parts = token.split('.');
      const payload = JSON.parse(atob(parts[1]));
      const exp = payload.exp;

      if (exp) {
        // Proportional refresh: min(2 minutes, 50% of token lifespan) before expiry
        const lifespan = getTokenLifespan(token);
        const refreshBeforeMs = Math.min(2 * 60 * 1000, lifespan * 500); // 50% of lifespan in ms
        const refreshTime = (exp * 1000) - Date.now() - refreshBeforeMs;

        const attemptRefreshWithRetry = async () => {
          const MAX_RETRIES = 3;
          const RETRY_DELAYS = [2000, 5000, 10000]; // 2s, 5s, 10s

          for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
            console.log(`[AuthnMe] Token refresh attempt ${attempt + 1}/${MAX_RETRIES}...`);
            const success = await refreshAccessToken();
            if (success) return;

            // If we still have a refresh token, wait and retry
            if (attempt < MAX_RETRIES - 1 && localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY)) {
              console.log(`[AuthnMe] Refresh attempt ${attempt + 1} failed, retrying in ${RETRY_DELAYS[attempt] / 1000}s...`);
              await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS[attempt]));
            }
          }

          // All retries exhausted - only logout if refresh token is gone (truly invalid)
          if (!localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY)) {
            console.log('[AuthnMe] Refresh token cleared (invalid), dropping local session');
            clearLocalSession();
          } else {
            console.log('[AuthnMe] All refresh attempts failed but refresh token still exists - will retry on next interval');
          }
        };

        if (refreshTime > 0) {
          console.log(`[AuthnMe] Scheduling token refresh in ${Math.round(refreshTime / 1000)}s`);
          const timeout = setTimeout(attemptRefreshWithRetry, refreshTime);
          return () => clearTimeout(timeout);
        } else {
          // Token already expired or will expire very soon, try to refresh immediately
          console.log('[AuthnMe] Token expired/expiring soon, refreshing immediately...');
          attemptRefreshWithRetry();
        }
      }
    } catch {
      // Ignore parse errors
    }
  }, [token, isAuthenticated, refreshAccessToken, clearLocalSession]);

  const login = useCallback(async () => {
    if (!effectiveOrgId) {
      setError('AuthnMe organization ID not configured');
      return;
    }

    // Redirect to AuthnMe login page with tenant_id for OAuth-style flow
    // Preserve current path + query so the user returns to the same page after auth
    const returnUri = effectiveRedirectUri.replace(/\/+$/, '') + window.location.pathname + window.location.search;
    const authUrl = new URL(`${AUTHNME_BASE_URL}/login`);
    authUrl.searchParams.set('tenant_id', effectiveOrgId);
    authUrl.searchParams.set('redirect_uri', returnUri);

    console.log('[AuthnMe] Login redirect:', {
      url: authUrl.toString(),
      orgId: effectiveOrgId,
      redirectUri: effectiveRedirectUri,
    });

    window.location.href = authUrl.toString();
  }, [effectiveOrgId, effectiveRedirectUri, AUTHNME_BASE_URL]);

  // Direct Google OAuth login
  const loginWithGoogle = useCallback(() => {
    if (!effectiveOrgId) {
      setError('AuthnMe organization ID not configured');
      return;
    }

    // Redirect directly to AuthnMe's Google OAuth endpoint
    const googleAuthUrl = new URL(`${AUTHNME_BASE_URL}/auth/google/init`);
    googleAuthUrl.searchParams.set('tenant_id', effectiveOrgId);
    googleAuthUrl.searchParams.set('redirect_uri', effectiveRedirectUri);

    window.location.href = googleAuthUrl.toString();
  }, [effectiveOrgId, effectiveRedirectUri, AUTHNME_BASE_URL]);

  // Email magic link login
  const loginWithEmail = useCallback(async (email: string): Promise<{ success: boolean; webauthnAvailable?: boolean; message?: string }> => {
    if (!effectiveOrgId) {
      setError('AuthnMe organization ID not configured');
      return { success: false, message: 'Organization ID not configured' };
    }

    try {
      const response = await fetch(`${AUTHNME_BASE_URL}/auth/email/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          tenant_id: effectiveOrgId,
          redirect_uri: effectiveRedirectUri,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, message: data.error || 'Failed to send magic link' };
      }

      // Check if user has WebAuthn available (passkey)
      if (data.webauthn_available) {
        return {
          success: true,
          webauthnAvailable: true,
          message: data.message || 'Passkey available for this account'
        };
      }

      return { success: true, message: data.message || 'Magic link sent! Check your email.' };
    } catch (err) {
      console.error('Email login error:', err);
      return { success: false, message: 'Failed to initiate email login' };
    }
  }, [effectiveOrgId, effectiveRedirectUri, AUTHNME_BASE_URL]);

  // Direct passkey (WebAuthn) login
  const loginWithPasskey = useCallback(async (): Promise<{ success: boolean; message?: string }> => {
    if (!effectiveOrgId) {
      setError('AuthnMe organization ID not configured');
      return { success: false, message: 'Organization ID not configured' };
    }

    // Check if WebAuthn is supported
    if (!window.PublicKeyCredential) {
      return { success: false, message: 'Passkeys are not supported on this device' };
    }

    try {
      // Step 1: Get authentication challenge from AuthnMe
      const initResponse = await fetch(`${AUTHNME_BASE_URL}/auth/webauthn/authenticate/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: effectiveOrgId,
        }),
      });

      if (!initResponse.ok) {
        const errorData = await initResponse.json().catch(() => ({}));
        return { success: false, message: errorData.error || 'Failed to start passkey authentication' };
      }

      const challengeData = await initResponse.json();

      // Step 2: Convert challenge data for WebAuthn API
      const publicKeyOptions: PublicKeyCredentialRequestOptions = {
        challenge: base64UrlToBuffer(challengeData.challenge),
        timeout: 60000,
        rpId: challengeData.rp_id || new URL(AUTHNME_BASE_URL).hostname.replace('api.', ''),
        userVerification: 'preferred',
        allowCredentials: challengeData.allow_credentials?.map((cred: { id: string; type: string }) => ({
          id: base64UrlToBuffer(cred.id),
          type: cred.type as PublicKeyCredentialType,
          transports: ['internal', 'hybrid'] as AuthenticatorTransport[],
        })),
      };

      // Step 3: Prompt user for passkey
      const credential = await navigator.credentials.get({
        publicKey: publicKeyOptions,
      }) as PublicKeyCredential;

      if (!credential) {
        return { success: false, message: 'Passkey authentication was cancelled' };
      }

      const response = credential.response as AuthenticatorAssertionResponse;

      // Step 4: Send credential to AuthnMe for verification
      const verifyResponse = await fetch(`${AUTHNME_BASE_URL}/auth/webauthn/authenticate/finish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: effectiveOrgId,
          credential_id: bufferToBase64Url(credential.rawId),
          client_data_json: bufferToBase64Url(response.clientDataJSON),
          authenticator_data: bufferToBase64Url(response.authenticatorData),
          signature: bufferToBase64Url(response.signature),
          user_handle: response.userHandle ? bufferToBase64Url(response.userHandle) : undefined,
          session_id: challengeData.session_id,
          redirect_uri: effectiveRedirectUri,
        }),
      });

      if (!verifyResponse.ok) {
        const errorData = await verifyResponse.json().catch(() => ({}));
        return { success: false, message: errorData.error || 'Passkey verification failed' };
      }

      const tokenData = await verifyResponse.json();

      // Step 5: Store tokens and update state
      if (tokenData.access_token) {
        storeToken(tokenData.access_token);
        if (tokenData.refresh_token) {
          localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, tokenData.refresh_token);
        }
        const claims = parseJwtClaims(tokenData.access_token);
        if (claims) {
          setToken(tokenData.access_token);
          setUser(claims);
          setIsAuthenticated(true);
          setHasEverLoggedIn(true);
          localStorage.setItem(HAS_EVER_LOGGED_IN_KEY, 'true');
          return { success: true, message: 'Signed in with passkey' };
        }
      }

      return { success: false, message: 'Failed to process authentication response' };
    } catch (err: any) {
      console.error('Passkey login error:', err);
      if (err.name === 'NotAllowedError') {
        return { success: false, message: 'Passkey authentication was cancelled or not allowed' };
      }
      return { success: false, message: err.message || 'Passkey authentication failed' };
    }
  }, [effectiveOrgId, effectiveRedirectUri, AUTHNME_BASE_URL]);

  // Check if token will expire within the given seconds (default 60s)
  const isTokenExpiringSoon = useCallback((tokenStr: string, withinSeconds = 60): boolean => {
    try {
      const parts = tokenStr.split('.');
      if (parts.length !== 3) return true;
      const payload = JSON.parse(atob(parts[1]));
      const exp = payload.exp;
      if (!exp) return false;
      return (exp * 1000) - Date.now() < (withinSeconds * 1000);
    } catch {
      return true;
    }
  }, []);

  // Ensure token is fresh - call this before critical operations
  // Returns true if token is valid, false if refresh failed and user should re-login
  const ensureTokenFresh = useCallback(async (): Promise<boolean> => {
    if (apiKey) {
      return true; // API keys don't expire; nothing to refresh
    }
    const currentToken = token || getStoredToken();

    if (!currentToken) {
      return false; // No token at all
    }

    // If token is expired or expiring within 30 seconds, refresh it
    if (isTokenExpired(currentToken) || isTokenExpiringSoon(currentToken, 30)) {
      console.log('[AuthnMe] Token expired/expiring, refreshing...');
      const success = await refreshAccessToken();
      if (!success) {
        console.log('[AuthnMe] Token refresh failed');
        return false;
      }
    }

    return true;
  }, [apiKey, token, isTokenExpiringSoon, refreshAccessToken]);

  const getAuthHeaders = useCallback((): Record<string, string> => {
    if (apiKey) {
      return { 'X-API-Key': apiKey };
    }
    const currentToken = token || getStoredToken();

    if (currentToken) {
      // Trigger background refresh if token is expiring soon
      // Proportional: min(2 minutes, 50% of token lifespan)
      const lifespan = getTokenLifespan(currentToken);
      const refreshThreshold = Math.min(120, lifespan * 0.5);
      if (isTokenExpiringSoon(currentToken, refreshThreshold)) {
        console.log(`[AuthnMe] Token expiring within ${refreshThreshold}s, triggering background refresh`);
        refreshAccessToken().catch(() => {
          // Ignore errors from background refresh
        });
      }

      return { 'Authorization': `Bearer ${currentToken}` };
    }
    return {};
  }, [apiKey, token, isTokenExpiringSoon, refreshAccessToken]);

  // Fetch wrapper that handles 401 by refreshing the token and retrying
  const fetchWithAuth = useCallback(async (url: string, options: RequestInit = {}): Promise<Response> => {
    // API-key session: nothing to refresh, just attach the key.
    if (apiKey) {
      const keyHeaders = new Headers(options.headers);
      keyHeaders.set('X-API-Key', apiKey);
      return fetch(url, { ...options, headers: keyHeaders });
    }

    // Get current token (might be stale if state hasn't updated)
    const currentToken = token || getStoredToken();

    // Check if token needs refresh before making request
    if (currentToken && isTokenExpired(currentToken)) {
      console.log('[AuthnMe] Token expired, refreshing before request...');
      const refreshed = await refreshAccessToken();
      if (!refreshed) {
        // Can't refresh, make request anyway (will likely 401)
        console.log('[AuthnMe] Refresh failed, proceeding with expired token');
      }
    }

    // Get the latest token after potential refresh
    const authToken = getStoredToken();

    // Merge auth headers
    const headers = new Headers(options.headers);
    if (authToken) {
      headers.set('Authorization', `Bearer ${authToken}`);
    }

    // Make the request
    let response = await fetch(url, { ...options, headers });

    // If 401, try to refresh and retry once
    if (response.status === 401 && localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY)) {
      console.log('[AuthnMe] Got 401, attempting token refresh...');
      const refreshed = await refreshAccessToken();

      if (refreshed) {
        // Get the new token and retry
        const newToken = getStoredToken();
        if (newToken) {
          headers.set('Authorization', `Bearer ${newToken}`);
          response = await fetch(url, { ...options, headers });
          console.log('[AuthnMe] Retried request after refresh, status:', response.status);
        }
      } else {
        // Refresh failed - only logout if refresh token was invalidated (cleared by refreshAccessToken on 401)
        if (!localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY)) {
          console.log('[AuthnMe] Refresh token invalid, dropping local session');
          clearLocalSession();
        } else {
          console.log('[AuthnMe] Refresh failed (transient error), returning 401 response without logout');
        }
      }
    }

    return response;
  }, [apiKey, token, refreshAccessToken, clearLocalSession]);

  // Periodic token refresh check for idle users
  // Interval is proportional: min(5 minutes, 80% of token lifespan)
  useEffect(() => {
    if (!isAuthenticated || apiKey) return;

    const currentLifespan = token ? getTokenLifespan(token) : 3600;
    const REFRESH_INTERVAL = Math.min(5 * 60 * 1000, currentLifespan * 800); // 80% of lifespan in ms

    const checkAndRefresh = async () => {
      const currentToken = token || getStoredToken();
      if (currentToken) {
        const lifespan = getTokenLifespan(currentToken);
        const checkThreshold = Math.min(180, lifespan * 0.5);
        if (isTokenExpiringSoon(currentToken, checkThreshold)) {
          console.log(`[AuthnMe] Periodic check: token expiring within ${checkThreshold}s, refreshing...`);
          const success = await refreshAccessToken();
          if (!success) {
            console.log('[AuthnMe] Periodic refresh failed, will retry next interval');
          }
        }
      } else if (localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY)) {
        // Token gone from state but refresh token exists - try to recover
        console.log('[AuthnMe] No access token but refresh token exists, attempting recovery...');
        await refreshAccessToken();
      }
    };

    const intervalId = setInterval(checkAndRefresh, REFRESH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [isAuthenticated, token, isTokenExpiringSoon, refreshAccessToken]);

  const value: AuthnMeContextType = {
    isAuthenticated,
    isLoading,
    isConfigured,
    hasEverLoggedIn,
    user,
    token,
    apiKey,
    authMethod: apiKey ? 'api_key' : (token ? 'jwt' : null),
    error,
    login,
    loginWithGoogle,
    loginWithEmail,
    loginWithPasskey,
    logout,
    getAuthHeaders,
    fetchWithAuth,
    refreshToken: refreshAccessToken,
    ensureTokenFresh,
  };

  return (
    <AuthnMeContext.Provider value={value}>
      {children}
    </AuthnMeContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

export function useAuthnMe(): AuthnMeContextType {
  const context = useContext(AuthnMeContext);
  if (!context) {
    throw new Error('useAuthnMe must be used within an AuthnMeProvider');
  }
  return context;
}

// ============================================================================
// Utility Hook for API Calls
// ============================================================================

/**
 * Hook to get headers for authenticated API calls.
 * Falls back to API key if provided and no JWT is available.
 */
export function useAuthHeaders(apiKey?: string): Record<string, string> {
  const context = useContext(AuthnMeContext);

  return useMemo<Record<string, string>>(() => {
    const headers: Record<string, string> = {};

    // If inside AuthnMeProvider and authenticated, use JWT.
    if (context?.isAuthenticated && context.token) {
      headers.Authorization = `Bearer ${context.token}`;
      return headers;
    }

    // Fall back to API key.
    if (apiKey) {
      headers['X-API-Key'] = apiKey;
    }

    return headers;
  }, [context?.isAuthenticated, context?.token, apiKey]);
}

// ============================================================================
// Higher-Order Component for Protected Routes
// ============================================================================

interface ProtectedRouteProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function ProtectedRoute({ children, fallback }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, login } = useAuthnMe();

  if (isLoading) {
    return <div className="auth-loading">Loading...</div>;
  }

  if (!isAuthenticated) {
    if (fallback) {
      return <>{fallback}</>;
    }
    // Auto-redirect to login
    login();
    return <div className="auth-loading">Redirecting to login...</div>;
  }

  return <>{children}</>;
}

export default AuthnMeProvider;
